<template>
  <div class="d3-chart-wrapper" style="position: relative;">
    <chart-toolbar
      :locked="!!lockedY"
      :zoomed="!!zoomRange"
      :lockable="!!tkDomainsSekarang"
      @lock="tkToggleLockY(tkDomainsSekarang)"
      @reset="tkResetZoom()"
      @png="tkExportPng('auxiliary-engine-usage')"
      @csv="tkExportCsvSekarang()"
    />
    <div ref="chartContainer" class="chart-container"></div>
    <div ref="tooltip" class="d3-tooltip" style="opacity: 0; pointer-events: none; position: absolute; background: rgba(0, 0, 0, 0.8); color: #fff; padding: 8px; border-radius: 4px; font-size: 12px; z-index: 10;"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatDateTime } from '@/util/date';
import { CHART_AXIS, CHART_TOOLTIP } from '@/util/apiDatetime';
import chartToolkit from './chartToolkit';
import ChartToolbar from './ChartToolbar.vue';

export default {
  name: 'AuxiliaryEngineUsage',
  mixins: [chartToolkit],
  components: { ChartToolbar },
  props: {
    series: { type: Array, default: () => [] },
    deviceStatus: { type: Object, default: () => ({}) },
    hideFuel: { type: Boolean, default: false },
  },
  data() {
    return {
      resizeObserver: null,
      tkDomainsSekarang: null,
    };
  },
  created() {
    this.tkTerakhir = null;
  },
  mounted() {
    this.tkBind();
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    this.tkUnbind();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    series: { deep: true, handler: 'drawChart' },
    deviceStatus: { deep: true, handler: 'drawChart' },
    hideFuel: 'drawChart',
  },
  methods: {
    // Reset mengembalikan KETIGA chart, bukan cuma yang ini.
    tkResetZoom() {
      this.tkPublishZoom(null);
    },
    tkExportCsvSekarang() {
      if (!this.tkTerakhir) return;
      this.tkExportCsv('auxiliary-engine-usage', this.tkTerakhir.timestamps, this.tkTerakhir.datasets);
    },
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;
          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }
            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;
              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },

    processData() {
      if (!this.series) return null;

      const mapData = (path) => this.series.map((data) => {
        const value = path.split('.').reduce((o, i) => (o ? o[i] : undefined), data);
        return Number.isFinite(Number(value)) ? Number(Number(value).toFixed(2)) : null;
      });

      const timestamps = this.series.map((d) => new Date(d.timestamp * 1000));

      // AE di-loop berdasarkan aeCount (tak terbatas 3). Tiap AE punya 1 dataset fuel
      // (sumbu kiri) + 1 dataset running seconds (sumbu kanan).
      const aeCount = this.deviceStatus?.aeCount || 0;
      const fuelPalette = ['#FF0000', '#00FF00', '#0000FF', '#FF9900', '#9900FF', '#00CCCC'];
      const runPalette = ['#FFC400', '#FF00FF', '#00FFFF', '#FF6600', '#6600FF', '#0099CC'];

      const datasets = [];
      if (!this.hideFuel) {
        for (let i = 1; i <= aeCount; i += 1) {
          const fuel = mapData(`aeData.AE${i}.fuelConsumption`);
          datasets.push({
            name: `AE ${i} Fuel (L)`,
            data: fuel,
            interpolated: this.interpolateNulls(fuel),
            color: fuelPalette[(i - 1) % fuelPalette.length],
            axis: 'left',
          });
        }
      }
      for (let i = 1; i <= aeCount; i += 1) {
        const sec = mapData(`aeData.AE${i}.runningSeconds`);
        datasets.push({
          name: `AE ${i} Run (s)`,
          data: sec,
          interpolated: this.interpolateNulls(sec),
          color: runPalette[(i - 1) % runPalette.length],
          axis: 'right',
        });
      }

      return { timestamps, datasets };
    },

    drawChart() {
      const processed = this.processData();
      if (!processed) return;
      const { timestamps, datasets } = processed;
      const tampil = this.tkVisible(datasets);
      const container = this.$refs.chartContainer;

      d3.select(container).selectAll('*').remove();

      const visibleWidth = this.$el.clientWidth || container.clientWidth;
      const isCompact = visibleWidth < 576;
      const chartOuterWidth = Math.max(container.clientWidth, isCompact ? 720 : 0);

      // Margin kanan desktop diseragamkan biar crosshair antar chart sejajar.
      const margin = {
        top: isCompact ? 42 : 28,
        right: isCompact ? 58 : 180,
        bottom: isCompact ? 48 : 50,
        left: isCompact ? 52 : 60,
      };
      const width = Math.max(1, chartOuterWidth - margin.left - margin.right);
      const chartHeight = isCompact ? 420 : 600;
      const height = chartHeight - margin.top - margin.bottom;
      const xTickCount = isCompact ? 5 : 8;
      const yTickCount = isCompact ? 5 : 10;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', chartOuterWidth)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Tanpa ini garis meluber ke luar kotak pas sumbu dikunci.
      const idKlip = `tk-klip-${this._uid}`;
      svg.append('defs').append('clipPath').attr('id', idKlip)
        .append('rect')
        .attr('width', width)
        .attr('height', height);
      const lapisData = svg.append('g').attr('clip-path', `url(#${idKlip})`);

      // X Axis
      const x = d3.scaleTime().domain(this.tkXDomain(timestamps)).range([0, width]);
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(xTickCount).tickFormat((d) => formatDateTime(d, CHART_AXIS, null, false)))
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');

      // Y Axis Left (Fuel)
      const leftDomain = this.tkDomain('left', tampil, 10);
      const [, maxFuel] = leftDomain;
      const yLeft = d3.scaleLinear().domain([0, maxFuel * 1.1]).range([height, 0]);
      svg.append('g').call(d3.axisLeft(yLeft).ticks(yTickCount))
        .append('text').attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', isCompact ? -36 : -40)
        .attr('dy', '0.71em')
        .attr('text-anchor', 'end')
        .text('Fuel Consumption (L)');

      // Y Axis Right (Seconds) — hanya digambar kalau ada dataset running seconds.
      const hasRightAxis = tampil.some((d) => d.axis === 'right');
      const rightDomain = this.tkDomain('right', tampil, 10);
      const [, maxSec] = rightDomain;
      const yRight = d3.scaleLinear().domain([0, maxSec * 1.1]).range([height, 0]);
      if (hasRightAxis) {
        svg.append('g').attr('transform', `translate(${width}, 0)`).call(d3.axisRight(yRight).ticks(yTickCount))
          .append('text')
          .attr('fill', '#000')
          .attr('transform', 'rotate(-90)')
          .attr('y', 40)
          .attr('dy', '0.71em')
          .attr('text-anchor', 'end')
          .text('Running Seconds');
      }

      // Grid
      svg.append('g').attr('class', 'grid').attr('opacity', 0.1).call(d3.axisLeft(yLeft).ticks(yTickCount).tickSize(-width).tickFormat(''));

      const getScale = (axisName) => (axisName === 'left' ? yLeft : yRight);

      // Simpan tuple mentah dari tkDomain, bukan .domain() yang udah dihias — biar kunci ga ikut menghias ulang.
      this.tkDomainsSekarang = { left: leftDomain, right: rightDomain };
      // Ekspor ngikut yang keliatan: kena zoom dan seri yang disembunyiin.
      const [xAwal, xAkhir] = x.domain();
      const indeksTampak = [];
      timestamps.forEach((t, i) => {
        if (t >= xAwal && t <= xAkhir) indeksTampak.push(i);
      });
      this.tkTerakhir = {
        timestamps: indeksTampak.map((i) => timestamps[i]),
        datasets: tampil.map((d) => ({ name: d.name, data: indeksTampak.map((i) => d.data[i]) })),
      };

      // Draw Lines & Dots
      const realLineWidth = 2.5;
      const hoverLineWidth = 4;
      const interpolatedLineWidth = 1.5;
      tampil.forEach((series, index) => {
        const yScale = getScale(series.axis);

        // Line Interpolated
        lapisData.append('path')
          .datum(series.interpolated)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', interpolatedLineWidth)
          .attr('stroke-dasharray', '4 4')
          .attr('opacity', 0.6)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)));

        // Line Real
        lapisData.append('path')
          .datum(series.data)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', realLineWidth)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)))
          .on('mouseover', function handleMouseOver() {
            d3.select(this).attr('stroke-width', hoverLineWidth);
          })
          .on('mouseout', function handleMouseOut() {
            d3.select(this).attr('stroke-width', realLineWidth);
          });

        // Dots for Real Data
        lapisData.selectAll(`.dot-series-${index}`)
          .data(series.data.map((d, i) => ({ value: d, date: timestamps[i] })).filter((d) => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', `dot-series-${index}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');
      });

      // Tooltip & Hover
      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;

      const focusGroup = svg.append('g').style('display', 'none');

      const focusLine = focusGroup.append('line')
        .attr('stroke', '#aaa').attr('stroke-dasharray', '3 3').attr('y1', 0)
        .attr('y2', height);

      const focusCircles = tampil.map((ds) => focusGroup.append('circle').attr('r', 5).attr('fill', ds.color).attr('stroke', '#fff')
        .attr('stroke-width', 2));

      this.tkOnHover((ms) => {
        const [awal, akhir] = x.domain();
        if (ms === null || ms < +awal || ms > +akhir) {
          focusGroup.style('display', 'none');
          return;
        }
        focusGroup.style('display', null);
        focusLine.attr('x1', x(new Date(ms))).attr('x2', x(new Date(ms)));
      });

      const legendSpacing = 18;
      const legendItemWidth = 160;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendWidth = Math.min(datasets.length, legendColumns) * legendItemWidth;
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - legendWidth) / 2)}, -10)`);
      const legendItem = legendGroup.selectAll('g')
        .data(datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * legendSpacing})`;
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          this.tkToggleSeries(d.name);
        });

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '12px')
        .text((d) => d.name);

      legendItem.select('rect').attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.3 : 1));
      legendItem.select('text')
        .attr('opacity', (d) => (this.hiddenSeries[d.name] ? 0.4 : 1))
        .style('text-decoration', (d) => (this.hiddenSeries[d.name] ? 'line-through' : 'none'));

      const alasHover = this.tkInstallBrush(svg, x, width, height);
      alasHover
        .on('mouseover', () => { tooltip.style('opacity', 1); focusGroup.style('display', null); })
        .on('mouseout', () => { tooltip.style('opacity', 0); focusGroup.style('display', 'none'); this.tkPublishHover(null); })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          // eslint-disable-next-line no-nested-ternary
          const index = i < timestamps.length && i > 0 ? (x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1) : 0;
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);
          this.tkPublishHover(+date);

          tampil.forEach((ds, idx) => {
            const val = ds.data[index];
            const yScale = getScale(ds.axis);
            if (val !== null && val !== undefined) {
              focusCircles[idx].attr('cx', xPos).attr('cy', yScale(val)).style('display', null);
            } else {
              focusCircles[idx].style('display', 'none');
            }
          });

          let html = `<strong>${formatDateTime(date, CHART_TOOLTIP)}</strong><br/>`;
          tampil.forEach((ds) => {
            const val = ds.data[index];
            if (val !== null && val !== undefined) {
              html += `<div style="display:flex; align-items:center; gap:5px;">
                          <span style="width:10px; height:10px; border-radius:50%; background:${ds.color}; display:inline-block;"></span>
                          ${ds.name}: ${val}
                        </div>`;
            }
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          const top = posY + 15;
          if (left + 180 > chartOuterWidth) left = posX - 190;

          tooltip.html(html).style('left', `${left}px`).style('top', `${top}px`);
        });
    },
  },
};
</script>

<style scoped>
.d3-chart-wrapper { font-family: inherit; background: #fff; }
/* d3-axis nempelin font-family="sans-serif" di grup axis; CSS menang atas atribut itu. */
.d3-chart-wrapper ::v-deep [font-family] { font-family: inherit; }
.chart-container { width: 100%; }

@media screen and (max-width: 575px) {
  .d3-chart-wrapper {
    min-width: 720px;
  }
}
</style>
